import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { useSolution } from '../../context/SolutionContext';

const Dendrograph = () => {
  const { state, dispatch } = useSolution();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ 
          width: rect.width || 800, 
          height: Math.max(rect.height, 600) 
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    const observer = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      observer.disconnect();
    };
  }, []);

  // Toggle collapse for a node
  const toggleCollapse = useCallback((nodeId) => {
    setCollapsedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Build hierarchy with collapse state applied
  const buildHierarchyData = useCallback(() => {
    if (!state.selectedComponents || state.selectedComponents.length === 0) {
      return null;
    }

    const root = {
      id: 'root',
      name: 'AI Solution',
      type: 'root',
      icon: '🎯',
      depth: 0,
      children: [],
      _children: null // Store hidden children here when collapsed
    };

    // Group by phase
    const phaseMap = new Map();

    state.selectedComponents.forEach(comp => {
      const phaseId = comp.phaseId;
      const layerId = comp.layerId;
      const subLayerId = comp.subLayerId;

      // Phase level
      if (!phaseMap.has(phaseId)) {
        phaseMap.set(phaseId, {
          id: `phase-${phaseId}`,
          name: comp.phaseName || 'Unknown Phase',
          type: 'phase',
          icon: getPhaseIcon(phaseId),
          depth: 1,
          children: new Map(),
          _children: null
        });
      }

      const phase = phaseMap.get(phaseId);

      // Layer level
      if (!phase.children.has(layerId)) {
        phase.children.set(layerId, {
          id: `layer-${layerId}`,
          name: comp.layerName || 'Unknown Layer',
          type: 'layer',
          icon: '📦',
          depth: 2,
          children: new Map(),
          _children: null
        });
      }

      const layer = phase.children.get(layerId);

      // Sub-Layer level
      if (!layer.children.has(subLayerId)) {
        layer.children.set(subLayerId, {
          id: `sublayer-${subLayerId}`,
          name: comp.subLayerName || 'Unknown Sub-Layer',
          type: 'sublayer',
          icon: '📁',
          depth: 3,
          children: [],
          _children: null
        });
      }

      const subLayer = layer.children.get(subLayerId);

      // Component level
      subLayer.children.push({
        id: `comp-${comp.id}`,
        name: comp.name,
        type: 'component',
        icon: comp.icon || '🔧',
        component: comp,
        depth: 4,
        children: null,
        _children: null
      });
    });

    // Convert Maps to arrays
    root.children = Array.from(phaseMap.values()).map(phase => ({
      ...phase,
      children: Array.from(phase.children.values()).map(layer => ({
        ...layer,
        children: Array.from(layer.children.values())
      }))
    }));

    // Apply collapse state
    const applyCollapse = (node) => {
      if (!node.children || node.children.length === 0) return;
      
      if (collapsedNodes.has(node.id)) {
        node._children = node.children;
        node.children = null;
      } else {
        node._children = null;
      }
      
      // Recursively apply to children
      if (node.children) {
        node.children.forEach(child => applyCollapse(child));
      } else if (node._children) {
        node._children.forEach(child => applyCollapse(child));
      }
    };

    applyCollapse(root);

    return root;
  }, [state.selectedComponents, collapsedNodes]);

  const getPhaseIcon = (phaseId) => {
    const icons = {
      'phase1': '🔧',
      'phase2': '💾',
      'phase3': '🔄',
      'phase4': '🧠',
      'phase5': '⚡',
      'phase6': '🎯',
      'phase7': '🛡️'
    };
    return icons[phaseId] || '📦';
  };

  // Render the dendrograph
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const hierarchyData = buildHierarchyData();
    
    if (!hierarchyData || !hierarchyData.children || hierarchyData.children.length === 0) {
      return;
    }

    const { width, height } = dimensions;
    const cx = width / 2;
    const cy = height / 2;

    // Create main group for zoom/pan
    const mainGroup = svg.append('g')
      .attr('transform', `translate(${transform.x},${transform.y}) scale(${transform.k})`);

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 3])
      .on('zoom', (event) => {
        setTransform(event.transform);
        mainGroup.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Create D3 hierarchy
    const d3Root = d3.hierarchy(hierarchyData);

    // Configure tree layout
    const treeLayout = d3.tree()
      .size([height - 150, width - 250])
      .nodeSize([60, 160]);

    const treeData = treeLayout(d3Root);

    // Add arrow marker
    svg.append('defs')
      .append('marker')
      .attr('id', 'arrow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#94a3b8');

    // Draw links
    const links = mainGroup.append('g')
      .selectAll('path')
      .data(treeData.links())
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('d', d => {
        const source = d.source;
        const target = d.target;
        const mx = (source.y + target.y) / 2;
        return `M${source.y},${source.x} C${mx},${source.x} ${mx},${target.x} ${target.y},${target.x}`;
      })
      .attr('fill', 'none')
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', d => {
        const widths = { 1: 3, 2: 2.5, 3: 2, 4: 1.5 };
        return widths[d.target.depth] || 1.5;
      })
      .attr('opacity', 0.6)
      .attr('marker-end', 'url(#arrow)');

    // Draw nodes
    const nodes = mainGroup.append('g')
      .selectAll('g')
      .data(treeData.descendants())
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.y},${d.x})`)
      .style('cursor', 'pointer');

    // Add node content
    nodes.each(function(d) {
      const group = d3.select(this);
      const radius = getNodeRadius(d);
      const hasChildren = d.children && d.children.length > 0;
      const isCollapsed = d.data._children && d.data._children.length > 0;
      const canCollapse = hasChildren || isCollapsed;
      
      // Main circle
      group.append('circle')
        .attr('r', radius)
        .attr('fill', getNodeColor(d))
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 2)
        .attr('class', 'node-circle');
      
      // Icon
      if (d.data.icon) {
        group.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .attr('font-size', getIconSize(d))
          .attr('pointer-events', 'none')
          .text(d.data.icon);
      }
      
      // Expand/Collapse button (separate clickable circle)
      if (canCollapse) {
        const btnX = radius + 15;
        const btnY = -radius - 10;
        
        // Button background
        group.append('circle')
          .attr('cx', btnX)
          .attr('cy', btnY)
          .attr('r', 10)
          .attr('fill', isCollapsed ? '#3b82f6' : '#ffffff')
          .attr('stroke', '#3b82f6')
          .attr('stroke-width', 2)
          .attr('class', 'expand-btn')
          .style('cursor', 'pointer')
          .on('click', (event) => {
            event.stopPropagation();
            toggleCollapse(d.data.id);
          });
        
        // Button icon (+ or -)
        group.append('text')
          .attr('x', btnX)
          .attr('y', btnY + 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '14px')
          .attr('font-weight', 'bold')
          .attr('fill', isCollapsed ? '#ffffff' : '#3b82f6')
          .attr('pointer-events', 'none')
          .text(isCollapsed ? '+' : '−');
      }
      
      // Label
      if (showLabels) {
        group.append('text')
          .attr('x', radius + 10)
          .attr('y', 4)
          .attr('font-size', getLabelSize(d))
          .attr('fill', '#475569')
          .attr('font-weight', d.data.type === 'component' ? '600' : '700')
          .attr('pointer-events', 'none')
          .text(d.data.name);
        
        // Count badge for collapsed nodes
        if (isCollapsed) {
          const count = countDescendants(d.data);
          group.append('text')
            .attr('x', radius + 10)
            .attr('y', 18)
            .attr('font-size', '10px')
            .attr('fill', '#94a3b8')
            .attr('pointer-events', 'none')
            .text(`${count} hidden items`);
        }
      }
    });

    // Count descendants
    function countDescendants(node) {
      let count = 0;
      const children = node.children || node._children || [];
      children.forEach(child => {
        count += 1 + countDescendants(child);
      });
      return count;
    }

    // Node click handler (for component details)
    nodes.on('click', (event, d) => {
      // Only trigger for component nodes
      if (d.data.type === 'component' && d.data.component) {
        event.stopPropagation();
        setSelectedNodeId(d.data.id);
        dispatch({ type: 'SHOW_COMPONENT_DETAIL', payload: d.data.component });
      }
    });

    // Hover effects
    nodes.on('mouseenter', (event, d) => {
      setHoveredNode(d);
      
      d3.select(event.currentTarget)
        .select('.node-circle')
        .attr('stroke', '#ef4444')
        .attr('stroke-width', 3);
      
      // Highlight connected links
      links
        .attr('stroke', l => 
          l.source === d || l.target === d ? '#ef4444' : '#94a3b8'
        );
    });

    nodes.on('mouseleave', (event, d) => {
      setHoveredNode(null);
      
      d3.select(event.currentTarget)
        .select('.node-circle')
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 2);
      
      links.attr('stroke', '#94a3b8');
    });

    // Tooltip
    const tooltip = d3.select(containerRef.current)
      .append('div')
      .attr('class', 'dendrograph-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', 'white')
      .style('border', '1px solid #e2e8f0')
      .style('border-radius', '8px')
      .style('padding', '12px')
      .style('box-shadow', '0 10px 25px rgba(0,0,0,0.1)')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('max-width', '300px');

    nodes.on('mousemove', (event, d) => {
      const [x, y] = d3.pointer(event, containerRef.current);
      
      let content = `
        <div style="font-weight: bold; margin-bottom: 8px;">
          ${d.data.icon || ''} ${d.data.name}
        </div>
        <div style="font-size: 12px; color: #64748b;">
          Type: ${d.data.type}
        </div>
      `;
      
      if (d.data.type === 'component' && d.data.component) {
        content += `
          <div style="font-size: 12px; color: #64748b; margin-top: 4px;">
            ${d.data.component.purpose || ''}
          </div>
        `;
      }
      
      const hasChildren = d.children && d.children.length > 0;
      const isCollapsed = d.data._children && d.data._children.length > 0;
      if (hasChildren || isCollapsed) {
        content += `
          <div style="font-size: 11px; color: #94a3b8; margin-top: 4px;">
            Click +/- to ${isCollapsed ? 'expand' : 'collapse'}
          </div>
        `;
      }
      
      tooltip
        .style('visibility', 'visible')
        .style('left', `${x + 15}px`)
        .style('top', `${y + 15}px`)
        .html(content);
    })
    .on('mouseout', () => {
      tooltip.style('visibility', 'hidden');
    });

    return () => {
      tooltip.remove();
    };

  }, [dimensions, state.selectedComponents, collapsedNodes, showLabels, transform, buildHierarchyData, toggleCollapse, dispatch]);

  // Helper functions
  const getNodeRadius = (d) => {
    const radii = {
      'root': 30,
      'phase': 25,
      'layer': 20,
      'sublayer': 16,
      'component': 12
    };
    return radii[d.data.type] || 12;
  };

  const getIconSize = (d) => {
    const sizes = {
      'root': 22,
      'phase': 18,
      'layer': 14,
      'sublayer': 11,
      'component': 9
    };
    return sizes[d.data.type] || 10;
  };

  const getLabelSize = (d) => {
    const sizes = {
      'root': 16,
      'phase': 14,
      'layer': 12,
      'sublayer': 11,
      'component': 10
    };
    return sizes[d.data.type] || 10;
  };

  const getNodeColor = (d) => {
    const colors = {
      'root': '#4ade80',
      'phase': '#3b82f6',
      'layer': '#f59e0b',
      'sublayer': '#ec4899',
      'component': '#8b5cf6'
    };
    return colors[d.data.type] || '#94a3b8';
  };

  const handleReset = () => {
    setTransform({ x: 0, y: 0, k: 1 });
    d3.select(svgRef.current)
      .transition()
      .duration(500)
      .call(d3.zoom().transform, d3.zoomIdentity);
  };

  const handleExpandAll = () => {
    setCollapsedNodes(new Set());
  };

  const handleCollapseAll = () => {
    const allNodeIds = new Set();
    state.selectedComponents.forEach(comp => {
      allNodeIds.add(`phase-${comp.phaseId}`);
      allNodeIds.add(`layer-${comp.layerId}`);
      allNodeIds.add(`sublayer-${comp.subLayerId}`);
    });
    setCollapsedNodes(allNodeIds);
  };

  const hasComponents = state.selectedComponents && state.selectedComponents.length > 0;

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full bg-gradient-to-br from-gray-50 to-gray-100"
      style={{ minHeight: '600px', position: 'relative' }}
    >
      {/* Toolbar */}
      <div className="absolute top-4 right-4 flex gap-2 z-20">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-1 flex gap-1">
          <button
            onClick={handleExpandAll}
            className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
            title="Expand All"
          >
            Expand All
          </button>
          <button
            onClick={handleCollapseAll}
            className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
            title="Collapse All"
          >
            Collapse All
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-1 flex gap-1">
          <button
            onClick={() => setShowLabels(!showLabels)}
            className={`px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
              showLabels ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100'
            }`}
          >
            Labels
          </button>
          <button
            onClick={handleReset}
            className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-2 z-20">
        <div className="text-sm font-semibold text-gray-800">
          {state.selectedComponents.length} Components
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-20">
        <div className="text-xs font-semibold mb-2">Hierarchy</div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full bg-green-400"></div>
          <span className="text-xs">Solution</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-xs">Phase</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <span className="text-xs">Layer</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full bg-pink-500"></div>
          <span className="text-xs">Sub-Layer</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-500"></div>
          <span className="text-xs">Component</span>
        </div>
      </div>

      {/* Empty State */}
      {!hasComponents && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center bg-white rounded-xl shadow-lg p-8 max-w-md">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Components Selected
            </h3>
            <p className="text-gray-500">
              Select components from the sidebar to visualize your AI solution
            </p>
          </div>
        </div>
      )}

      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        className="w-full h-full"
        style={{ cursor: hasComponents ? 'grab' : 'default' }}
      />
    </div>
  );
};

export default Dendrograph;